import { gql, useMutation } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { Refresh } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TRIGGER_SYNCHRONOUS_INTEGRATION = gql`
  mutation triggerSynchronousIntegration(
    $input: TriggerSynchronousIntegrationInput!
  ) {
    triggerSynchronousIntegration(input: $input) {
      workflowCompletion {
        id
      }
    }
  }
`;

export const SynchronousIntegration = ({
  onNext,
  onBack,
  workflowCompletion,
  subStepId,
  options,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  subStepId: string;
  options: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation('workflowCompletions');
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerIntegration] = useMutation(TRIGGER_SYNCHRONOUS_INTEGRATION, {
    onCompleted: () => {
      setError(null);
      if (onNext) {
        onNext();
      }
    },
  });

  const attemptIntegration = async () => {
    setIsLoading(true);
    setError(null);

    const maxAttempts = options.allowAutoRetry ? 3 : 1;
    for (let attempt = 1; attempt <= maxAttempts; ++attempt) {
      try {
        await triggerIntegration({
          variables: {
            input: {
              workflowCompletionId: workflowCompletion.id,
              subStepId,
            },
            skipErrorHandler: true,
          },
        });
        break;
      } catch (err) {
        if (attempt === maxAttempts) {
          setError(err as Error);
          setIsLoading(false);
        }
      }
    }

    setIsLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: integrations initially fire on mount
  useEffect(() => {
    // When navigating backward, skip the integration and continue going back
    if (direction === 'BACKWARD') {
      onBack();
      return;
    }
    attemptIntegration();
  }, []);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={1}
      minHeight='300px'
    >
      <Typography variant='headingMedium'>
        {translateBackend(options.title) || t('synchronousIntegration.title')}
      </Typography>
      {options.subtitle && (
        <Typography variant='bodyMedium' sx={{ mb: 4 }}>
          {translateBackend(options.subtitle)}
        </Typography>
      )}
      {error && (
        <Box mb={2}>
          <Alert severity='error'>{error.message}</Alert>
          {options.allowManualRetry && (
            <Box mt={2} display='flex' justifyContent='center'>
              <Button
                onClick={attemptIntegration}
                disabled={isLoading}
                label={t('shared:retry')}
                variant='text'
                leadingIcon={Refresh}
              />
            </Box>
          )}
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
